import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { ArrowRight, CheckCircle, Users, Target, Zap, Shield, Award, Star, Settings, BarChart3, Workflow, HeadphonesIcon, Code, Smartphone, Laptop, ChevronRight, Download, Calendar, Mail, MessageCircle, TrendingUp, Building, Monitor, FileText, HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { SEO } from "../components/SEO";

export default function ServicesPage() {
  const faqData = [
    {
      question: "How long does a typical Zoho implementation take?",
      answer: "Implementation timelines vary based on complexity and scope. A single application setup takes 2-4 weeks, while comprehensive multi-app implementations can take 6-16 weeks. We provide detailed project timelines during our initial consultation, including milestones and deliverables for each phase."
    },
    {
      question: "Do you provide training for our team?",
      answer: "Yes! Comprehensive training is included in all our packages. We provide user training, administrator training, and create custom training materials. Our training includes hands-on sessions, video tutorials, and ongoing support to ensure your team can effectively use the system."
    },
    {
      question: "What ongoing support do you provide after implementation?",
      answer: "We offer various support packages including 24/7 technical support, regular system health checks, performance optimization, user assistance, and help with new feature adoption. Our support ranges from 30 days (Starter) to 6 months (Enterprise) with ongoing support packages available."
    },
    {
      question: "Can you integrate Zoho with our existing systems?",
      answer: "Absolutely! We specialize in integrating Zoho applications with various third-party systems including ERPs, payment gateways, e-commerce platforms, accounting software, and custom applications. We use APIs, webhooks, and custom connectors to ensure seamless data flow."
    },
    {
      question: "What is your pricing model and what's included?",
      answer: "Our pricing is transparent and project-based. It includes consultation, implementation, configuration, data migration, training, and support. We provide detailed quotes after understanding your requirements. No hidden costs - everything is clearly outlined in our proposals."
    },
    {
      question: "How do you handle data migration from our current systems?",
      answer: "We have a proven data migration process that includes data assessment, cleaning, mapping, and secure transfer. We ensure data integrity and provide backup solutions. Our team handles everything from simple CSV imports to complex database migrations with zero data loss guarantee."
    },
    {
      question: "What makes your Zoho implementation different from others?",
      answer: "Our certified expertise, proven methodology, and focus on business outcomes set us apart. We don't just configure software - we optimize your business processes. With 500+ successful implementations and 98% client satisfaction, we deliver results that matter."
    },
    {
      question: "Can you help us choose the right Zoho applications?",
      answer: "Yes! We conduct a thorough business analysis to recommend the optimal Zoho applications for your needs. We consider your industry, business size, processes, and growth plans to create a tailored solution that maximizes ROI and efficiency."
    },
    {
      question: "Do you provide custom development services?",
      answer: "Yes, we offer custom development using Zoho Creator, custom functions, and API integrations. Our team can build custom applications, create specialized workflows, and develop unique solutions that aren't available out-of-the-box."
    },
    {
      question: "What industries do you specialize in?",
      answer: "We have expertise across multiple industries including e-commerce, manufacturing, professional services, healthcare, real estate, and education. Our industry-specific experience helps us implement best practices and optimize Zoho for your sector's unique requirements."
    }
  ];
  // FAQPage schema for the FAQ section
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
  const serviceCategories = [
    {
      id: "implementation",
      title: "Implementation",
      icon: Settings,
      description: "Complete Zoho setup and configuration"
    },
    {
      id: "analytics",
      title: "Analytics & BI", 
      icon: BarChart3,
      description: "Data insights and business intelligence"
    },
    {
      id: "automation",
      title: "Automation",
      icon: Workflow,
      description: "Process automation and workflows"
    },
    {
      id: "support",
      title: "Support",
      icon: HeadphonesIcon,
      description: "Ongoing maintenance and optimization"
    }
  ];

  const detailedServices = {
    implementation: [
      {
        title: "Zoho CRM Implementation",
        description: "Complete CRM setup with lead management, sales pipeline, and customer tracking",
        features: ["Lead & Contact Management", "Sales Pipeline Setup", "Email Integration", "Custom Fields & Layouts"],
        duration: "2-4 weeks",
        price: "₹75,000 - ₹1,50,000",
        complexity: 70
      },
      {
        title: "Zoho One Ecosystem",
        description: "Full suite implementation with all Zoho applications integrated seamlessly",
        features: ["40+ Application Setup", "Cross-app Integration", "Single Sign-On", "Data Synchronization"],
        duration: "6-12 weeks",
        price: "₹3,00,000 - ₹8,00,000", 
        complexity: 95
      },
      {
        title: "Zoho Creator Applications",
        description: "Custom business applications built on Zoho Creator platform",
        features: ["Custom App Development", "Database Design", "User Interface Creation", "Workflow Automation"],
        duration: "3-8 weeks",
        price: "₹1,00,000 - ₹3,00,000",
        complexity: 85
      }
    ],
    analytics: [
      {
        title: "Business Intelligence Setup",
        description: "Advanced analytics and reporting with Zoho Analytics",
        features: ["Custom Dashboards", "KPI Tracking", "Automated Reports", "Data Visualization"],
        duration: "1-3 weeks",
        price: "₹50,000 - ₹1,20,000",
        complexity: 60
      },
      {
        title: "Data Integration Hub",
        description: "Connect multiple data sources for comprehensive business insights",
        features: ["Multi-source Data Import", "Real-time Sync", "Data Cleansing", "Advanced Analytics"],
        duration: "2-4 weeks",
        price: "₹80,000 - ₹2,00,000",
        complexity: 80
      }
    ],
    automation: [
      {
        title: "Workflow Automation",
        description: "Automate repetitive tasks and business processes across Zoho apps",
        features: ["Process Mapping", "Automated Workflows", "Approval Systems", "Notification Setup"],
        duration: "1-2 weeks",
        price: "₹30,000 - ₹80,000",
        complexity: 50
      },
      {
        title: "Third-party Integrations",
        description: "Connect Zoho with external applications and services",
        features: ["API Integration", "Data Synchronization", "Custom Connectors", "Real-time Updates"],
        duration: "2-5 weeks",
        price: "₹60,000 - ₹1,80,000",
        complexity: 75
      }
    ],
    support: [
      {
        title: "24/7 Technical Support",
        description: "Round-the-clock assistance for all your Zoho applications",
        features: ["Issue Resolution", "Performance Monitoring", "User Support", "System Maintenance"],
        duration: "Ongoing",
        price: "₹15,000/month",
        complexity: 40
      },
      {
        title: "Optimization & Training",
        description: "Continuous improvement and user training programs",
        features: ["Performance Optimization", "Feature Updates", "User Training", "Best Practices"],
        duration: "Ongoing",
        price: "₹25,000/month",
        complexity: 55
      }
    ]
  };

  const processSteps = [
    {
      step: 1,
      title: "Discovery & Consultation",
      description: "In-depth analysis of your business requirements and current systems",
      icon: Users,
      timeline: "Week 1",
      deliverables: ["Requirements Document", "Current State Analysis", "Technical Assessment", "Project Roadmap"]
    },
    {
      step: 2,
      title: "Solution Architecture",
      description: "Design comprehensive solution architecture tailored to your needs",
      icon: Building,
      timeline: "Week 1-2",
      deliverables: ["System Architecture", "Integration Plan", "Data Migration Strategy", "Security Framework"]
    },
    {
      step: 3,
      title: "Development & Configuration",
      description: "Configure Zoho applications and develop custom solutions",
      icon: Code,
      timeline: "Week 2-6",
      deliverables: ["Configured Applications", "Custom Workflows", "Integration Development", "User Interface Setup"]
    },
    {
      step: 4,
      title: "Testing & Quality Assurance",
      description: "Comprehensive testing to ensure everything works perfectly",
      icon: Shield,
      timeline: "Week 6-7",
      deliverables: ["Test Cases", "Performance Reports", "Security Audit", "Bug Fixes"]
    },
    {
      step: 5,
      title: "Training & Documentation",
      description: "User training and comprehensive documentation",
      icon: FileText,
      timeline: "Week 7-8",
      deliverables: ["User Training", "Admin Training", "Documentation", "Video Tutorials"]
    },
    {
      step: 6,
      title: "Go-Live & Support",
      description: "Launch support and ongoing optimization services",
      icon: Zap,
      timeline: "Week 8+",
      deliverables: ["Production Deployment", "Go-Live Support", "Performance Monitoring", "Ongoing Maintenance"]
    }
  ];

  const industryExpertise = [
    {
      industry: "E-commerce & Retail",
      icon: Smartphone,
      challenges: ["Inventory Management", "Order Processing", "Customer Service", "Multi-channel Sales"],
      solutions: ["Zoho Inventory + Commerce Integration", "Automated Order Workflows", "Customer Support Portal", "Analytics Dashboard"],
      results: "300% faster order processing, 40% cost reduction, 95% inventory accuracy",
      clients: "50+ E-commerce businesses",
      image: "https://images.unsplash.com/photo-1556742393-d75f468bfcb0?w=400&h=300&fit=crop"
    },
    {
      industry: "Manufacturing",
      icon: Settings,
      challenges: ["Production Planning", "Quality Control", "Supply Chain Management", "Equipment Tracking"],
      solutions: ["Zoho Creator Manufacturing Apps", "Production Workflows", "Quality Management System", "Supply Chain Automation"],
      results: "25% reduction in production costs, 60% improvement in quality control",
      clients: "30+ Manufacturing companies",
      image: "https://images.unsplash.com/photo-1581092921461-eab62e97a780?w=400&h=300&fit=crop"
    },
    {
      industry: "Professional Services",
      icon: Laptop,
      challenges: ["Project Management", "Time Tracking", "Client Communication", "Billing Automation"],
      solutions: ["Zoho Projects Integration", "Time Tracking Automation", "Client Portal Setup", "Automated Billing Workflows"],
      results: "40% improvement in project delivery, 50% reduction in billing time",
      clients: "100+ Service providers",
      image: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=400&h=300&fit=crop"
    },
    {
      industry: "Healthcare",
      icon: HeadphonesIcon,
      challenges: ["Patient Management", "Appointment Scheduling", "Medical Records", "Compliance"],
      solutions: ["Patient Management System", "Automated Scheduling", "Secure Records Management", "Compliance Workflows"],
      results: "60% improvement in patient satisfaction, 30% reduction in administrative tasks",
      clients: "25+ Healthcare providers",
      image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=400&h=300&fit=crop"
    },
    {
      industry: "Real Estate",
      icon: Building,
      challenges: ["Lead Management", "Property Listings", "Client Follow-up", "Document Management"],
      solutions: ["CRM for Real Estate", "Property Management Portal", "Automated Follow-ups", "Document Automation"],
      results: "200% increase in lead conversion, 70% faster deal closure",
      clients: "40+ Real Estate agencies",
      image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400&h=300&fit=crop"
    },
    {
      industry: "Education",
      icon: Monitor,
      challenges: ["Student Management", "Course Administration", "Fee Management", "Communication"],
      solutions: ["Student Information System", "Course Management Portal", "Fee Automation", "Parent-Teacher Communication"],
      results: "80% reduction in administrative work, 90% parent satisfaction",
      clients: "20+ Educational institutions",
      image: "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=400&h=300&fit=crop"
    }
  ];

  const servicePackages = [
    {
      name: "Starter Package",
      subtitle: "Perfect for Small Businesses",
      price: "₹50,000",
      originalPrice: "₹75,000",
      duration: "2-4 weeks",
      description: "Essential Zoho setup to get your business started with digital transformation",
      features: [
        "Single Zoho application setup (CRM/Books/Inventory)",
        "Basic customization and configuration",
        "Data migration (up to 1,000 records)",
        "2 hours of user training",
        "30 days of technical support",
        "Standard reporting setup",
        "Email integration",
        "Mobile app configuration"
      ],
      limitations: ["Limited to 1 application", "Basic customization only", "Standard support"],
      popular: false,
      savings: "Save ₹25,000"
    },
    {
      name: "Business Package",
      subtitle: "Most Popular Choice",
      price: "₹1,50,000",
      originalPrice: "₹2,00,000",
      duration: "4-8 weeks",
      description: "Comprehensive solution for growing businesses with multiple Zoho applications",
      features: [
        "3-5 Zoho applications integration",
        "Advanced customization & workflows",
        "Data migration (up to 10,000 records)",
        "8 hours of comprehensive training",
        "90 days of premium support",
        "Custom reporting and dashboards",
        "Third-party integrations (2 included)",
        "Automation workflows setup",
        "Performance optimization",
        "Documentation and user guides"
      ],
      limitations: [],
      popular: true,
      savings: "Save ₹50,000"
    },
    {
      name: "Enterprise Package",
      subtitle: "Complete Digital Transformation",
      price: "Custom Pricing",
      originalPrice: "",
      duration: "8-16 weeks",
      description: "Full-scale transformation for large organizations with complex requirements",
      features: [
        "Complete Zoho ecosystem implementation",
        "Advanced integrations & custom development",
        "Unlimited data migration",
        "Comprehensive training program (20+ hours)",
        "6 months of dedicated support",
        "Dedicated project manager",
        "Custom application development",
        "Advanced security configuration",
        "Multi-location setup",
        "24/7 priority support",
        "Quarterly optimization reviews",
        "White-glove onboarding"
      ],
      limitations: [],
      popular: false,
      savings: "Best Value"
    }
  ];

  

  const whyChooseUs = [
    {
      icon: Award,
      title: "Certified Zoho Experts",
      description: "Official Zoho certifications and 5+ years of hands-on experience",
      metric: "15+ Certifications"
    },
    {
      icon: TrendingUp,
      title: "Proven Track Record",
      description: "500+ successful implementations with 98% client satisfaction rate",
      metric: "98% Success Rate"
    },
    {
      icon: Zap,
      title: "Rapid Implementation",
      description: "Streamlined processes for faster deployment with minimal disruption",
      metric: "50% Faster Setup"
    },
    {
      icon: Shield,
      title: "Enterprise Security",
      description: "Bank-grade security implementation with compliance standards",
      metric: "100% Secure"
    },
    {
      icon: Users,
      title: "Dedicated Support",
      description: "24/7 technical support with dedicated project managers",
      metric: "24/7 Available"
    },
    {
      icon: Target,
      title: "ROI Focused",
      description: "Solutions designed to deliver measurable business results",
      metric: "3x ROI Average"
    }
  ];

  return (
    <>
      <SEO
        title="Zoho Services – Implementation, Automation & Support | Optiwizer"
        description="Explore our full range of Zoho services: CRM, Analytics, Automation, Support, and more. Tailored solutions for every business need."
        canonical="https://optiwizer.com/services"
        ogImage="/assets/logos/optiwizer-logo.png"
      />
      {/* FAQPage Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      {/* Hero Section */}
      <section className="pt-24 lg:pt-28 pb-20 bg-gradient-to-br from-blue-50 via-white to-indigo-50 relative overflow-hidden" aria-labelledby="services-hero-heading">
        <div className="absolute inset-0 bg-grid-gray-100/50 bg-[size:20px_20px] opacity-60"></div>
        <div className="absolute top-10 left-10 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-400/10 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-800 rounded-full px-4 py-2 mb-6">
              <Star className="h-4 w-4" />
              <span className="text-sm font-medium">Comprehensive Zoho Solutions</span>
            </div>
            
            <h1 id="services-hero-heading" className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              Transform Your Business with
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 block">
                Expert Zoho Services
              </span>
            </h1>
            
            <p className="text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto mb-10 leading-relaxed">
              From implementation to optimization, we provide end-to-end Zoho solutions 
              that streamline operations, boost productivity, and accelerate your business growth.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
              <Link to="/contact">
                <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-lg px-10 py-4 shadow-xl">
                  <Calendar className="mr-2 h-5 w-5" />
                  Book Free Consultation
                </Button>
              </Link>
              
              <Link to="/contact">
                <Button size="lg" variant="outline" className="text-lg px-10 py-4 border-2 hover:bg-gray-50">
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Get Custom Quote
                </Button>
              </Link>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-blue-600 mb-2">500+</div>
                <div className="text-gray-600">Projects Delivered</div>
              </div>
              <div className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-purple-600 mb-2">98%</div>
                <div className="text-gray-600">Client Satisfaction</div>
              </div>
              <div className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-indigo-600 mb-2">5+</div>
                <div className="text-gray-600">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-green-600 mb-2">24/7</div>
                <div className="text-gray-600">Support Available</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Categories */}
      <section className="py-20 bg-white" aria-labelledby="service-categories-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-blue-100 text-blue-800">Service Categories</Badge>
            <h2 id="service-categories-heading" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Zoho Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our specialized service offerings designed to address every aspect 
              of your Zoho implementation and optimization journey.
            </p>
          </div>

          <Tabs defaultValue="implementation" className="w-full">
            <div className="mb-12">
              <TabsList className="w-full h-auto p-0 bg-transparent gap-0">
                <div className="grid w-full grid-cols-2 lg:grid-cols-4 gap-2 bg-gray-50 p-2 rounded-2xl">
                  {serviceCategories.map((category) => (
                    <TabsTrigger 
                      key={category.id} 
                      value={category.id}
                      className="flex flex-col sm:flex-row items-center justify-center gap-2 py-4 px-4 rounded-xl transition-all duration-300 data-[state=active]:bg-white data-[state=active]:shadow-lg data-[state=active]:text-blue-600 hover:bg-white/50 h-auto min-h-[4rem]"
                    >
                      <category.icon className="h-5 w-5 flex-shrink-0" />
                      <span className="font-medium text-center text-sm sm:text-base">{category.title}</span>
                    </TabsTrigger>
                  ))}
                </div>
              </TabsList>
            </div>

            {Object.entries(detailedServices).map(([categoryId, services]) => (
              <TabsContent key={categoryId} value={categoryId} className="mt-0">
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
                  {services.map((service, index) => (
                    <Card key={index} className="group hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-0 shadow-lg bg-white">
                      <CardHeader className="pb-4">
                        <div className="flex items-start justify-between mb-4">
                          <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors leading-tight">
                            {service.title}
                          </CardTitle>
                          <Badge className="bg-blue-50 text-blue-700 shrink-0 ml-3 px-3 py-1">
                            {service.duration}
                          </Badge>
                        </div>
                        <p className="text-gray-600 leading-relaxed">
                          {service.description}
                        </p>
                      </CardHeader>

                      <CardContent className="space-y-6">
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-3">Key Features:</h3>
                          <div className="space-y-2">
                            {service.features.map((feature, idx) => (
                              <div key={idx} className="flex items-center space-x-3">
                                <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                                <span className="text-sm text-gray-600">{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">Complexity Level</span>
                            <span className="text-sm text-gray-600">{service.complexity}%</span>
                          </div>
                          <Progress value={service.complexity} className="h-2" />
                        </div>

                        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Starting from</div>
                            <div className="font-bold text-blue-600 text-lg">{service.price}</div>
                          </div>
                          <Link to="/contact">
                            <Button className="bg-blue-600 hover:bg-blue-700 text-white shadow-md">
                              Get Quote
                              <ChevronRight className="ml-1 h-4 w-4" />
                            </Button>
                          </Link>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50" aria-labelledby="why-choose-us-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-purple-100 text-purple-800">Why Choose Us</Badge>
            <h2 id="why-choose-us-heading" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Your Trusted Zoho Implementation Partner
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              With certified expertise and proven methodologies, we deliver Zoho solutions 
              that transform businesses and drive sustainable growth.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((feature, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0 shadow-lg bg-white">
                <CardContent className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl mb-6 group-hover:scale-110 transition-transform">
                    <feature.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {feature.description}
                  </p>
                  <Badge className="bg-blue-50 text-blue-700 font-semibold">
                    {feature.metric}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-20 bg-white" aria-labelledby="implementation-process-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-green-100 text-green-800">Our Process</Badge>
            <h2 id="implementation-process-heading" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Proven Implementation Methodology
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our structured 6-phase approach ensures successful Zoho implementations 
              with minimal disruption to your business operations.
            </p>
          </div>

          <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-x-8 lg:gap-y-20 gap-y-10">
              {processSteps.map((step, index) => (
                <Card key={index} className="relative hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0 shadow-lg">
                  {/* Step Circle */}
                  <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 flex items-center justify-center w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full text-white font-bold text-lg shadow-lg z-10">
                    {step.step}
                  </div>

                  <CardContent className="pt-12 p-8">
                    <div className="text-center mb-6">
                      <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 rounded-2xl mb-4">
                        <step.icon className="h-8 w-8 text-blue-600" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                      <Badge variant="outline" className="mb-3">{step.timeline}</Badge>
                      <p className="text-gray-600 leading-relaxed">{step.description}</p>
                    </div>

                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3 text-sm">Key Deliverables:</h3>
                      <ul className="space-y-1">
                        {step.deliverables.map((item, idx) => (
                          <li key={idx} className="flex items-center space-x-2">
                            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                            <span className="text-xs text-gray-600">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Industry Expertise */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50" aria-labelledby="industry-expertise-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-orange-100 text-orange-800">Industry Expertise</Badge>
            <h2 id="industry-expertise-heading" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Specialized Solutions for Every Industry
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Deep industry knowledge and sector-specific implementations that address 
              unique challenges and leverage proven best practices.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {industryExpertise.map((industry, index) => (
              <Card key={index} className="group hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-0 shadow-lg overflow-hidden">
                <div className="relative">
                  <img
                    src={industry.image}
                    alt={`Industry: ${industry.industry}`}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <div className="flex items-center space-x-2 mb-2">
                      <industry.icon className="h-6 w-6" />
                      <h3 className="font-bold text-xl">{industry.industry}</h3>
                    </div>
                    <Badge className="bg-white/20 text-white border-white/30">
                      {industry.clients}
                    </Badge>
                  </div>
                </div>

                <CardContent className="p-8">
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3">Common Challenges:</h3>
                      <div className="grid grid-cols-2 gap-2">
                        {industry.challenges.map((challenge, idx) => (
                          <div key={idx} className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                            <span className="text-sm text-gray-600">{challenge}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3">Our Solutions:</h3>
                      <div className="grid grid-cols-1 gap-2">
                        {industry.solutions.map((solution, idx) => (
                          <div key={idx} className="flex items-center space-x-2">
                            <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                            <span className="text-sm text-gray-600">{solution}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <TrendingUp className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="font-semibold text-green-800 text-sm mb-1">Proven Results</div>
                          <div className="text-green-700 text-sm">{industry.results}</div>
                        </div>
                      </div>
                    </div>

                    <Link to="/contact">
                      <Button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white">
                        View Industry Case Studies
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Service Packages */}
      <section className="py-20 bg-white" aria-labelledby="service-packages-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-yellow-100 text-yellow-800">Service Packages</Badge>
            <h2 id="service-packages-heading" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Choose the Perfect Package for Your Business
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transparent pricing with comprehensive packages designed to meet businesses 
              at every stage of their Zoho journey.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {servicePackages.map((pkg, index) => (
              <Card key={index} className={`relative hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-0 shadow-lg ${
                pkg.popular ? 'ring-2 ring-blue-500 scale-105' : ''
              }`}>
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <Badge className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 text-sm font-semibold">
                      Most Popular
                    </Badge>
                  </div>
                )}
                
                <CardHeader className="text-center pb-4">
                  <div className="mb-4">
                    <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                      {pkg.name}
                    </CardTitle>
                    <p className="text-sm text-gray-600">{pkg.subtitle}</p>
                  </div>
                  
                  <div className="mb-4">
                    <div className="flex items-baseline justify-center space-x-2">
                      <span className="text-4xl font-bold text-blue-600">{pkg.price}</span>
                      {pkg.originalPrice && (
                        <span className="text-xl text-gray-400 line-through">{pkg.originalPrice}</span>
                      )}
                    </div>
                    {pkg.savings && (
                      <Badge className="mt-2 bg-green-100 text-green-800">{pkg.savings}</Badge>
                    )}
                    <div className="text-sm text-gray-600 mt-2">{pkg.duration}</div>
                  </div>
                  
                  <p className="text-gray-600 text-sm leading-relaxed">{pkg.description}</p>
                </CardHeader>

                <CardContent className="pt-0">
                  <div className="space-y-4 mb-8">
                    <h3 className="font-semibold text-gray-900">What's Included:</h3>
                    <ul className="space-y-2">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    {pkg.limitations.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <h5 className="text-xs font-medium text-gray-500 mb-2">Package Limitations:</h5>  
                        <ul className="space-y-1">
                          {pkg.limitations.map((limitation, idx) => (
                            <li key={idx} className="text-xs text-gray-500">• {limitation}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  <Link to="/contact">
                    <Button 
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                      size="lg"
                    >
                      Get Started Today
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                  
                  <div className="text-center mt-4">
                    <Link to="/contact">
                      <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700">
                        <MessageCircle className="mr-2 h-4 w-4" />
                        Discuss Custom Requirements
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">
              Need a custom solution? We create tailored packages for unique requirements.
            </p>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-2">
                Request Custom Quote
                <Mail className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50" aria-labelledby="faq-heading">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-blue-100 text-blue-800">
              <HelpCircle className="mr-2 h-4 w-4" />
              Frequently Asked Questions
            </Badge>
            <h2 id="faq-heading" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Know
            </h2>
            <p className="text-xl text-gray-600">
              Get quick answers to common questions about our Zoho implementation 
              and consulting services.
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqData.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border-0">
                <Card className="shadow-md hover:shadow-lg transition-shadow duration-300">
                  <AccordionTrigger className="px-6 py-4 hover:no-underline">
                    <span className="text-left font-semibold text-gray-900 pr-4">
                      {faq.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </Card>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-6">
              Still have questions? Our Zoho experts are here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Ask Your Question
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-slate-900 via-blue-900 to-purple-900 relative overflow-hidden" aria-labelledby="final-cta-heading">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 mb-8">
            <Zap className="h-5 w-5 text-yellow-400" />
            <span className="text-white">Ready to Transform Your Business?</span>
          </div>
          
          <h2 id="final-cta-heading" className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Start Your Zoho Journey Today
          </h2>
          <p className="text-xl text-blue-100 mb-10 leading-relaxed">
            Join hundreds of businesses that have accelerated their growth with our expert Zoho implementations. 
            Your transformation journey starts with a simple conversation.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Link to="/contact">
              <Button size="lg" className="bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white border-0 text-lg px-10 py-4 shadow-2xl">
                <Calendar className="mr-2 h-5 w-5" />
                Book Free Consultation
              </Button>
            </Link>
            <div className="flex items-center space-x-4">
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10 text-lg px-10 py-4">
                <Download className="mr-2 h-5 w-5" />
                Service Brochure
              </Button>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-2xl mx-auto">
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-2">500+</div>
              <div className="text-blue-200 text-sm">Successful Projects</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-2">98%</div>
              <div className="text-blue-200 text-sm">Client Satisfaction</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-2">24/7</div>
              <div className="text-blue-200 text-sm">Expert Support</div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}